/**
 * InterviewCycleSettingsModal.tsx
 *
 * V55.61: 면담주기 설정 모달
 *
 * 고용형태별 면담 주기를 설정합니다.
 * - 수습사원 / 파견직 / 계약직 / 인턴 / 정규직
 * - 주기: 없음 ~ 12개월
 * - 수습 만료 30일 알림 옵션
 */

import React, { useState, useEffect } from 'react';
import { X, Settings, AlertTriangle, Loader } from 'lucide-react';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';
import {
  useInterviewCycleSettings,
  InterviewCycleSettings,
  CycleMonths,
  CYCLE_OPTIONS,
  DEFAULT_CYCLE_SETTINGS,
} from '../../../shared/hooks/useInterviewCycleSettings';

interface InterviewCycleSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * 면담주기 설정 모달
 */
export function InterviewCycleSettingsModal({
  isOpen,
  onClose,
}: InterviewCycleSettingsModalProps) {
  const { settings, isLoading, saveSettings, isSaving } = useInterviewCycleSettings();

  // 폼 상태
  const [formData, setFormData] = useState<InterviewCycleSettings>(DEFAULT_CYCLE_SETTINGS);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 설정 로드 시 폼 초기화
  useEffect(() => {
    if (!isLoading) {
      setFormData(settings);
    }
  }, [settings, isLoading]);

  // 모달 닫힐 때 에러 초기화
  useEffect(() => {
    if (!isOpen) {
      setSaveError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // 주기 변경 핸들러
  const handleCycleChange = (field: keyof InterviewCycleSettings, value: CycleMonths) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // 체크박스 변경 핸들러
  const handleCheckboxChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, probationAlertEnabled: checked }));
  };

  // 저장 핸들러
  const handleSave = async () => {
    setSaveError(null);

    try {
      await saveSettings(formData);
      onClose();
    } catch (err: any) {
      setSaveError(err.message || '저장에 실패했습니다.');
    }
  };

  // 드롭다운 렌더링
  const renderCycleSelect = (
    field: keyof InterviewCycleSettings,
    label: string,
    description?: string
  ) => {
    const value = formData[field] as CycleMonths;

    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <label
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--color-gray-700)',
            }}
          >
            {label}
          </label>
          {description && (
            <span
              style={{
                fontSize: '12px',
                color: 'var(--color-gray-500)',
                marginLeft: '8px',
              }}
            >
              {description}
            </span>
          )}
        </div>
        <select
          value={value === null ? 'null' : String(value)}
          onChange={(e) => {
            const val = e.target.value === 'null' ? null : Number(e.target.value) as CycleMonths;
            handleCycleChange(field, val);
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid var(--color-gray-300)',
            borderRadius: '6px',
            background: 'white',
            color: 'var(--color-gray-900)',
          }}
        >
          {CYCLE_OPTIONS.map((opt) => (
            <option key={opt.value === null ? 'null' : opt.value} value={opt.value === null ? 'null' : opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <ModalOverlay
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1000,
        }}
        onClose={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
          zIndex: 1001,
          width: '480px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Settings size={20} style={{ color: 'var(--color-gray-600)' }} />
            <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-gray-900)' }}>
              면담주기 설정
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: 'var(--color-gray-500)',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px',
                color: 'var(--color-gray-500)',
              }}
            >
              <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '8px' }}>로딩 중...</span>
            </div>
          ) : (
            <>
              {/* 안내 문구 */}
              <div
                style={{
                  padding: '12px 16px',
                  background: 'var(--color-blue-50)',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  color: 'var(--color-blue-700)',
                  lineHeight: '1.5',
                }}
              >
                고용형태별 면담 주기를 설정하세요.<br />
                설정된 주기에 따라 직원 목록의 면담 상태가 자동 계산됩니다.
              </div>

              {/* 수습사원 섹션 */}
              <div
                style={{
                  background: 'var(--color-gray-50)',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                }}
              >
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--color-gray-700)',
                    marginBottom: '12px',
                  }}
                >
                  수습사원 설정
                </div>

                {/* 수습 만료 30일 알림 체크박스 */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    padding: '12px',
                    background: 'var(--color-orange-50)',
                    borderRadius: '6px',
                    border: '1px solid var(--color-orange-200)',
                  }}
                >
                  <input
                    type="checkbox"
                    id="probationAlert"
                    checked={formData.probationAlertEnabled}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    style={{ marginTop: '2px' }}
                  />
                  <label
                    htmlFor="probationAlert"
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-orange-800)',
                      cursor: 'pointer',
                      lineHeight: '1.4',
                    }}
                  >
                    <strong>수습 만료 30일 전 알림</strong>
                    <br />
                    <span style={{ fontSize: '12px', color: 'var(--color-orange-600)' }}>
                      수습만료일 30일 전부터 '수습면담 임박' 상태로 표시됩니다.
                      이 옵션이 켜지면 고용형태 주기보다 우선 표시됩니다.
                    </span>
                  </label>
                </div>
              </div>

              {/* 일반 고용형태 섹션 */}
              <div
                style={{
                  background: 'var(--color-gray-50)',
                  borderRadius: '8px',
                  padding: '16px',
                }}
              >
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--color-gray-700)',
                    marginBottom: '4px',
                  }}
                >
                  고용형태별 면담 주기
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--color-gray-500)',
                    marginBottom: '12px',
                  }}
                >
                  *면담 주기가 설정되면 면담 2주(14일)전 상태값이 표기됩니다.
                </div>

                {renderCycleSelect('cycleDispatch', '파견직')}
                {renderCycleSelect('cycleContract', '계약직')}
                {renderCycleSelect('cycleIntern', '인턴')}
                {renderCycleSelect('cycleRegular', '정규직')}
              </div>

              {/* 에러 메시지 */}
              {saveError && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    background: 'var(--color-red-50)',
                    borderRadius: '6px',
                    marginTop: '16px',
                    color: 'var(--color-red-700)',
                    fontSize: '13px',
                  }}
                >
                  <AlertTriangle size={16} />
                  {saveError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '16px 20px',
            borderTop: '1px solid var(--color-gray-200)',
          }}
        >
          <button
            onClick={onClose}
            disabled={isSaving}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              background: 'var(--color-gray-100)',
              border: 'none',
              borderRadius: '6px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              color: 'var(--color-gray-700)',
            }}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || isSaving}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              background: isSaving ? 'var(--color-blue-400)' : 'var(--color-blue-600)',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading || isSaving ? 'not-allowed' : 'pointer',
              color: 'white',
              fontWeight: 500,
            }}
          >
            {isSaving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </>
  );
}
